import { useState, useEffect, useRef, useCallback } from 'react'
import { DateTime } from 'luxon'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import { SESSION_STATUS, formatPhone } from 'services/Whatsapp'
import MainButton from 'components/Buttons/MainButton'
import AlertDialog from 'components/AlertDialog'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import useSyncing from './useSyncing'

import {
  Card,
  CardTitle,
  Caption,
  Row,
  StatusDot,
  Warning,
  ErrorText,
  QrImage,
  TermsBox,
  Switch,
  SyncBanner
} from './styles'
import CheckBox from 'components/CheckBox'

/* O QR do WhatsApp troca a cada 20 a 60 segundos: a tela pergunta de novo a cada 3. */
export const POLL_MS = 3000

/*
  Conexão do número do salão: termo de aceite, QR que se atualiza, status, a chave do
  atendimento automático e desconectar. O bot nasce desligado: ligar é sempre uma escolha do
  salão, depois de conectar.
*/
export default function Connection({ businessId, session, onSessionChange }) {
  const [accepted, setAccepted] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [qr, setQr] = useState(null)
  const [bot, setBot] = useState(null)
  const [savingBot, setSavingBot] = useState(false)
  const [handedToTeam, setHandedToTeam] = useState(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState(null)
  const timer = useRef(null)

  const sessionUrl = `/businesses/${businessId}/whatsapp/session`
  const status = session?.status ?? null
  const waitingQr = status === 'SCAN_QR_CODE' || status === 'STARTING'

  const refresh = useCallback(async () => {
    try {
      const { data } = await cutileiApi.get(sessionUrl)
      onSessionChange(data)
      if (data.status === 'SCAN_QR_CODE') {
        const { data: image } = await cutileiApi.get(`${sessionUrl}/qr`)
        setQr(image)
      } else {
        setQr(null)
      }
    } catch (err) {
      if (err?.response?.status === 404) onSessionChange(null)
      // QR pedido no instante em que o celular leu: a próxima volta mostra o status novo
      else if (err?.response?.status !== 409) logApiError(err, 'whatsapp.session')
    }
  }, [sessionUrl, onSessionChange])

  // Enquanto espera o QR, pergunta de novo; conectado, para
  useEffect(() => {
    if (!waitingQr) return undefined
    timer.current = setInterval(refresh, POLL_MS)
    return () => clearInterval(timer.current)
  }, [waitingQr, refresh])

  useEffect(() => {
    if (status !== 'WORKING') return
    cutileiApi.get(`/businesses/${businessId}/whatsapp/bot`)
      .then(({ data }) => setBot(data.auto_reply_enabled))
      .catch(err => logApiError(err, 'whatsapp.bot'))
  }, [status, businessId])

  const handleConnect = async () => {
    setConnecting(true)
    setError(null)
    try {
      const { data } = await cutileiApi.post(sessionUrl)
      onSessionChange(data)
      await refresh()
    } catch (err) {
      logApiError(err, 'whatsapp.connect')
      setError(getApiErrorMessage(err, 'Não foi possível conectar. Tente de novo.'))
    } finally {
      setConnecting(false)
    }
  }

  const handleToggleBot = async () => {
    const next = !bot
    setSavingBot(true)
    setError(null)
    try {
      const { data } = await cutileiApi.put(`/businesses/${businessId}/whatsapp/bot`, { auto_reply_enabled: next })
      setBot(data.auto_reply_enabled)
      setHandedToTeam(next ? data.handed_to_team ?? 0 : null)
    } catch (err) {
      logApiError(err, 'whatsapp.bot')
      setError(getApiErrorMessage(err, 'Não foi possível mudar o atendimento automático.'))
    } finally {
      setSavingBot(false)
    }
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    setError(null)
    try {
      await cutileiApi.delete(sessionUrl)
      onSessionChange(null)
      setBot(null)
      setQr(null)
      setAccepted(false)
    } catch (err) {
      logApiError(err, 'whatsapp.disconnect')
      setError(getApiErrorMessage(err, 'Não foi possível desconectar. Tente de novo.'))
    } finally {
      setDisconnecting(false)
    }
  }

  const statusInfo = SESSION_STATUS[status] ?? SESSION_STATUS.STARTING
  const syncing = useSyncing(session)

  // Sem número conectado: termo de aceite e o botão de conectar
  if (!session) {
    return (
      <Card>
        <CardTitle>Conectar o WhatsApp do salão</CardTitle>
        <Caption>
          O bot responde as clientes pelo número do salão, conectado como um aparelho a mais (igual
          ao WhatsApp Web). O celular continua funcionando normalmente.
        </Caption>
        <Warning>
          <b>Antes de conectar:</b> o WhatsApp não tem uma forma oficial para este tipo de conexão
          e pode bloquear o número se entender que é automação. Use um número de teste ou o número
          do salão sabendo desse risco. As mensagens ficam no servidor do WhatsApp da Cutilei para a
          equipe ver as conversas aqui; elas não são usadas para nada além do atendimento do salão
          (LGPD). Você pode desconectar quando quiser.
        </Warning>
        <TermsBox>
          <CheckBox value={accepted} onValueChange={setAccepted}/>
          Li e aceito os riscos e o uso dos dados para o atendimento do salão.
        </TermsBox>
        {error && <ErrorText>{error}</ErrorText>}
        <Row>
          <MainButton label='Conectar' width={140} disabled={!accepted} loading={connecting} onClick={handleConnect}/>
        </Row>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <Row $justify='space-between'>
          <CardTitle>WhatsApp do salão</CardTitle>
          <Row $gap={6}>
            <StatusDot $color={statusInfo.color}/>
            <Caption>{statusInfo.label}</Caption>
          </Row>
        </Row>

        {syncing && (
          <SyncBanner role='status'>
            <Loading/>
            <span>
              Sincronizando as conversas do celular. Pode levar alguns minutos; as conversas vão
              aparecendo na aba Conversas.
            </span>
          </SyncBanner>
        )}

        {status === 'WORKING' && (
          <Caption>
            Número {formatPhone(session.phone?.replace(/^55/, '')) || 'conectado'}
            {session.connected_at && `, desde ${DateTime.fromISO(session.connected_at).toFormat("dd/MM 'às' HH:mm")}`}.
          </Caption>
        )}

        {waitingQr && (
          <>
            <Caption>
              No celular do salão, abra o WhatsApp &gt; Aparelhos conectados &gt; Conectar aparelho e
              aponte para o código. Ele troca sozinho a cada poucos segundos.
            </Caption>
            {qr ? (
              <QrImage alt='QR para conectar o WhatsApp' src={qr.data?.startsWith('data:') ? qr.data : `data:${qr.mimetype};base64,${qr.data}`}/>
            ) : (
              <Caption>Gerando o QR...</Caption>
            )}
          </>
        )}

        {(status === 'FAILED' || status === 'STOPPED') && (
          <>
            <Warning>
              A conexão com o WhatsApp caiu e o bot não está respondendo. Tente reconectar; se pedir,
              escaneie o QR de novo.
            </Warning>
            <Row>
              <MainButton label='Reconectar' width={140} loading={connecting} onClick={handleConnect}/>
            </Row>
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <Row>
          <MainButton label='Desconectar' dangerous width={140} loading={disconnecting} onClick={() => setConfirmDisconnect(true)}/>
        </Row>
      </Card>

      {status === 'WORKING' && (
        <Card>
          <Row $justify='space-between'>
            <CardTitle>Atendimento automático para todas as conversas</CardTitle>
            <Switch
              type='button'
              role='switch'
              aria-checked={!!bot}
              aria-label='Atendimento automático'
              $on={!!bot}
              disabled={bot === null || savingBot}
              onClick={handleToggleBot}
            />
          </Row>
          <Caption>
            {bot
              ? 'O bot responde as conversas novas e as marcadas como Bot. Ele passa a conversa para a equipe quando a cliente pede uma pessoa ou quando não entende. Quem já conversava com o salão continua com a equipe.'
              : 'Desligado: o bot só responde as conversas que você marcar como Bot na aba Conversas. Bom para testar com uma conversa só.'}
          </Caption>
          {handedToTeam > 0 && (
            <Caption>
              {handedToTeam === 1 ? '1 conversa que já existia ficou' : `${handedToTeam} conversas que já existiam ficaram`} com a
              equipe. Você pode passar para o bot na aba Conversas.
            </Caption>
          )}
        </Card>
      )}

      <AlertDialog
        visible={confirmDisconnect}
        title='Desconectar o WhatsApp'
        message='O bot para de responder e o número sai dos aparelhos conectados. Para voltar, será preciso escanear o QR de novo.'
        confirmText='Desconectar'
        cancelText='Cancelar'
        dangerous
        onConfirm={handleDisconnect}
        onClose={() => setConfirmDisconnect(false)}
      />
    </>
  )
}
